/**
 * EditWishModal Component for Admin
 * Modal wrapper for editing wishes
 */

import { useState } from 'react';
import Modal from '../../common/Modal';
import AdminWishEditForm from './AdminWishEditForm';
import type { AdminWishFormData } from './AdminWishEditForm';
import './EditModal.css';

const API_BASE = (import.meta.env as { VITE_API_URL?: string }).VITE_API_URL || 'http://localhost:8787';

interface Wish {
  id: string;
  title: string;
  description?: string;
  category: string;
  region: string;
  district?: string;
  budget?: string | null;
  urgent?: boolean;
  userName?: string;
  status?: string;
}

interface EditWishModalProps {
  isOpen: boolean;
  onClose: () => void;
  wish: Wish | null;
  onSave: (updatedItem?: Wish) => void;
}

const EditWishModal = ({ isOpen, onClose, wish, onSave }: EditWishModalProps) => {
  const [isLoading, setIsLoading] = useState(false);

  const handleSubmit = async (data: AdminWishFormData) => {
    if (!wish) return;

    setIsLoading(true);

    try {
      const token = localStorage.getItem('auth_token');
      const response = await fetch(`${API_BASE}/api/admin/content/wishes/${wish.id}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`,
        },
        body: JSON.stringify(data),
      });

      if (!response.ok) {
        const result = await response.json();
        throw new Error(result.error?.message || `HTTP ${response.status}`);
      }

      const result = await response.json();
      if (result.success) {
        // Pass updated item data to onSave for local update
        onSave(result.data);
        onClose();
      } else {
        throw new Error(result.error?.message || '更新失敗');
      }
    } catch (err) {
      // Error is silently ignored since AdminWishEditForm doesn't support error display
      console.error('Wish update error:', err);
    } finally {
      setIsLoading(false);
    }
  };

  if (!wish) return null;

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title="編輯許願"
      size="large"
    >
      <AdminWishEditForm
        initialData={{
          title: wish.title,
          description: wish.description,
          category: wish.category,
          region: wish.region,
          district: wish.district,
          budget: wish.budget ?? undefined,
          urgent: wish.urgent,
        }}
        onSubmit={handleSubmit}
        isLoading={isLoading}
      />
    </Modal>
  );
};

export default EditWishModal;
